import { useAppDispatch, useAppSelector, useAppStore } from 'app/hooks';
import {
  PickKind,
  pickSystemNameFor
} from 'components/equipment-page/equipment-comparison-utils';
import { SelectedSystemType, SystemOptionType } from 'data/types';
import {
  useDeleteQuotableSystemMutation,
  useSaveQuotableSystemMutation
} from 'features/api/project-api';
import { addPtacSystemToQuotable } from 'features/ptac-selection/ptac-selection-thunks';
import {
  addSelectedSystem,
  addSystemToCompare,
  noteComparisonRemovedUniqueIds,
  removeSelectedSystemsByKeys,
  removeSystemFromCompare,
  setActiveSystemType,
  updateSelectedSystem
} from 'features/systems';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';
import { removalKeysForSystem } from 'utils/comparison-removal';
import { getConfirmationMessagesForSystem } from 'utils/confirmation-rule-functions';
import { insufficientCoverageWarningModal } from 'utils/insufficient-coverage-warning-modal';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';
import { extractProductsFromSystem } from 'utils/systems';
import { thrownErrorMessage } from 'utils/thrown-error-message';
import { errorModal } from 'utils/ui';
import { confirmationInfoModal } from 'utils/validation';

const applyPickName = (
  option: SystemOptionType,
  kind: PickKind | null | undefined
): SystemOptionType => {
  if (!kind) return option;
  const pickName = pickSystemNameFor(kind);
  if (!pickName) return option;
  return { ...option, name: pickName };
};

const buildSelectedSystem = (
  option: SystemOptionType,
  uniqueId: string,
  kind?: PickKind | null
): SelectedSystemType => {
  const renamed = applyPickName(option, kind);
  return {
    ...renamed,
    uniqueId,
    configuration: {
      ...renamed.configuration,
      condensers: renamed.configuration.condensers.map((cond) => ({
        ...cond,
        validSystems: []
      }))
    },
    selectedAccessories: [],
    quotableAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    quoteStatus: 'unsent'
  };
};

export function useComparisonSelection() {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const [saveQuotableSystem] = useSaveQuotableSystemMutation({
    selectFromResult: () => ({})
  });
  const [deleteQuotableSystem] = useDeleteQuotableSystemMutation({
    selectFromResult: () => ({})
  });

  const isInComparison = useCallback(
    (systemId: string) => systemsToCompare.some((s) => s.id === systemId),
    [systemsToCompare]
  );

  const removeSystem = useCallback(
    async (systemId: string) => {
      const { selectedSystems, systemsToCompare: comparedSystems } =
        store.getState().systems;
      const compared = comparedSystems.find(
        (system) => system.id === systemId || system.uniqueId === systemId
      );
      const keys = removalKeysForSystem(systemId, compared);
      const matches = selectedSystems.filter(
        (system) => keys.includes(system.id) || keys.includes(system.uniqueId)
      );
      const uniqueIds = Array.from(
        new Set(matches.map((system) => system.uniqueId).filter(Boolean))
      );

      dispatch(removeSystemFromCompare(compared?.id ?? systemId));
      dispatch(removeSelectedSystemsByKeys(keys));
      if (uniqueIds.length > 0) {
        dispatch(noteComparisonRemovedUniqueIds(uniqueIds));
      }

      await Promise.all(
        uniqueIds.map(async (uniqueId) => {
          try {
            await deleteQuotableSystem(uniqueId).unwrap();
          } catch (error) {
            console.error('Failed to delete quotable system:', error);
          }
        })
      );
    },
    [dispatch, store, deleteQuotableSystem]
  );

  const addSystemsToComparison = useCallback(
    async (
      systems: SystemOptionType[],
      { warnAboutCoverage = true }: { warnAboutCoverage?: boolean } = {}
    ): Promise<void> => {
      systems.forEach((system) => dispatch(addSystemToCompare(system)));
      if (warnAboutCoverage) {
        await insufficientCoverageWarningModal(systems);
      }
    },
    [dispatch]
  );

  const addSystem = useCallback(
    async (
      option: SystemOptionType,
      kind?: PickKind | null,
      options?: { skipConfirmations?: boolean }
    ) => {
      const state = store.getState();
      if (state.systems.systemsToCompare.some((s) => s.id === option.id)) {
        return;
      }
      const renamedOption = applyPickName(option, kind);
      const warnAboutCoverage = !options?.skipConfirmations;
      const existingSelected = state.systems.selectedSystems.find(
        (s) => s.id === renamedOption.id
      );
      if (existingSelected) {
        if (kind && existingSelected.name !== renamedOption.name) {
          dispatch(
            updateSelectedSystem({
              uniqueId: existingSelected.uniqueId,
              name: renamedOption.name
            })
          );
        }
        dispatch(setActiveSystemType('ductless'));
        await addSystemsToComparison([renamedOption], { warnAboutCoverage });
        return;
      }

      const isPtac = isPtacSelectedSystem(renamedOption);
      const newUniqueId = nanoid(5);

      if (!isPtac) {
        dispatch(
          addSelectedSystem(
            buildSelectedSystem(renamedOption, newUniqueId, kind)
          )
        );
      }
      dispatch(setActiveSystemType('ductless'));
      await addSystemsToComparison([renamedOption], { warnAboutCoverage });

      if (!options?.skipConfirmations) {
        const matchingConfiguration = store
          .getState()
          .configurations.configurations.find(
            (c) => c.uniqueId === renamedOption.configuration.uniqueId
          );
        if (matchingConfiguration) {
          const confirmationMessages = getConfirmationMessagesForSystem(
            matchingConfiguration,
            renamedOption
          );
          if (confirmationMessages.length > 0) {
            await confirmationInfoModal(confirmationMessages);
          }
        }
      }

      try {
        if (isPtac) {
          await dispatch(addPtacSystemToQuotable(renamedOption)).unwrap();
        } else {
          await saveQuotableSystem({
            uniqueId: newUniqueId,
            configurationUniqueId: renamedOption.configuration.uniqueId,
            products: extractProductsFromSystem(renamedOption)
          }).unwrap();
        }
      } catch (error) {
        console.error('Failed to save quotable system:', error);
        if (isPtac) {
          dispatch(removeSystemFromCompare(renamedOption.id));
          const message = thrownErrorMessage(error);
          errorModal(
            message
              ? `Failed to add the PTAC system: ${message}`
              : 'Failed to add the PTAC system. Please try again.'
          );
        }
      }
    },
    [dispatch, store, saveQuotableSystem, addSystemsToComparison]
  );

  const toggleSystem = useCallback(
    async (option: SystemOptionType, kind?: PickKind | null) => {
      if (isInComparison(option.id)) {
        await removeSystem(option.id);
      } else {
        await addSystem(option, kind);
      }
    },
    [isInComparison, removeSystem, addSystem]
  );

  return {
    toggleSystem,
    removeSystem,
    addSystem,
    addSystemsToComparison,
    isInComparison
  };
}
